import {
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  IBodyProfile,
  useGetStorageFileUrl,
} from "../../api/profile";
import {
  COLOR_ONBOARDING_STATUS,
  ONBOARDING_STATUS_LABEL,
  OnboardingStatus,
  STATUS
} from "../../App.constants";
import CloudArrowUp from "../../common/images/png/CloudArrowUp.png";
import CopyIcon from "../../common/images/png/Copy.png";
import { ReactComponent as CheckSelectIcon } from "../../common/images/svg/CheckSelect.svg";
import { ReactComponent as SuccessIcon } from "../../common/images/svg/Success.svg";
import { ReactComponent as ArrowIcon } from "../../common/images/svg/SuffixSelectIcon.svg";
import { ReactComponent as TrashIcon } from '../../common/images/svg/Trash.svg';
import { CustomButton } from "../../components/button/buttonCustom";
import { ButtonOutlinedCustom } from "../../components/button/buttonOulined";
import { useNotification } from "../../components/providers/notification/useNotification";
import { companyRegistrationNumberRules } from "../../contants/addCompany.constants";
import {
  addressRules,
  clearFieldErrorMessageOnChange,
  companyNameRules,
  emailRules,
  websiteRules,
} from "../../utils/form.constants";
import { useMetadataCountries } from "../../utils/hooks/useMetadataCountries";
import useResponsive from "../../utils/hooks/useResponsive";
import { usePermission } from "../../store/use-permission";

/** Statuses that allow the user to upload or re-upload the onboarding form */
const ONBOARDING_CAN_UPLOAD = [
  OnboardingStatus.PENDING,
  OnboardingStatus.INVITED,
  OnboardingStatus.SUBMITTED,
  OnboardingStatus.REJECTED,
];

interface IDetailInfoProps {
  form: FormInstance<IBodyProfile>;
  onSubmit: (values: any) => void;
  info: any;
  isDirty: boolean;
  setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModalControl: React.Dispatch<React.SetStateAction<boolean>>;
  isAllFieldsFilled: boolean;
  setIsAllFieldsFilled: React.Dispatch<React.SetStateAction<boolean>>;
  checkAllFieldsFilled: () => boolean;
  isSuccess: boolean;
  /** Onboarding form: status from /profile */
  onboardingStatus?: string | null;
  /** Onboarding form file uuid */
  onboarding_form_uuid?: string | null;
  /** Display name of uploaded file (optional, from profile) */
  onboarding_file_name?: string | null;
  /** Called when user selects a file (upload happens on Submit) */
  onSelectOnboardingForm?: (file: File) => void;
  /** Called when user removes the selected (pending) onboarding file */
  onRemoveOnboardingForm?: () => void;
  /** Called when user removes the existing (server) onboarding file */
  onRemoveExistingOnboardingForm?: () => void;
  /** True when user removed existing file and has not selected a new one */
  onboardingFormRemoved?: boolean;
  /** File name of selected-but-not-yet-uploaded file */
  pendingOnboardingFileName?: string | null;
  isUploadingOnboarding?: boolean;
}

const ALLOWED_ONBOARDING_FILE_TYPES = [".pdf"];
const MAX_ONBOARDING_FILE_SIZE_MB = 10;

const DetailInfo = ({
  form,
  onSubmit,
  info,
  setIsDirty,
  isDirty,
  setShowModalControl,
  isAllFieldsFilled,
  setIsAllFieldsFilled,
  checkAllFieldsFilled,
  isSuccess,
  onboardingStatus,
  onboarding_form_uuid,
  onboarding_file_name,
  onSelectOnboardingForm,
  onRemoveOnboardingForm,
  onRemoveExistingOnboardingForm,
  onboardingFormRemoved,
  pendingOnboardingFileName,
  isUploadingOnboarding,
}: IDetailInfoProps) => {
  const navigate = useNavigate();
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification();
  const onboardingUploadRef = useRef<HTMLInputElement>(null);
  const [disabledInput, setDisabledInput] = useState(false);
  const [searchCountry, setSearchCountry] = useState<string>("");
  const [copied, setCopied] = useState(false);

  const { metadataCountries } = useMetadataCountries();

  const { data: onboardingFileUrl } = useGetStorageFileUrl(onboarding_form_uuid);

  const { permission: canUpdateProfile } = usePermission() as { permission: boolean };
  const canUploadOnboarding = Boolean(
    onboardingStatus &&
    (ONBOARDING_CAN_UPLOAD as readonly string[]).includes(onboardingStatus) &&
    onSelectOnboardingForm
  );
  /** Disable upload when: cannot upload by status, uploading in progress, or no update permission */

  const isOnboardingUploadDisabled =
    !canUploadOnboarding || !!isUploadingOnboarding || !canUpdateProfile;
  const hasOnboardingFile = Boolean(onboarding_form_uuid);
  const hasPendingOnboardingFile = Boolean(pendingOnboardingFileName);

  const onboardingDisplayName =
    onboarding_file_name ||
    pendingOnboardingFileName;

  const onboardingFileHref =
    onboardingFileUrl?.data?.data?.url ?? onboardingFileUrl?.data?.url;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(info.smartUID);
    setCopied(true);
    if (openNotification) {
      openNotification?.({
        key: "add_bank_success",
        message: "Copied SMART UID successfully!",
        type: "success",
      });
    }
  };

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value);
  };

  useEffect(() => {
    if (!info) return;
    form.setFieldsValue({
      email: info.email,
      fullName: info.fullName,
      regNo: info.regNo || "",
      country: info.country || "",
      address: info?.address,
      website: info.website,
      smartUID: info.smartUID,
    });
    setSearchCountry(info.country || "");
    setDisabledInput(info?.worldCheckVerification?.status === STATUS.LOW_RISK);
  }, [info]);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const beforeUploadOnboarding = useCallback(
    (file: File) => {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_ONBOARDING_FILE_TYPES.includes(ext)) {
        openNotification?.({
          key: "onboarding_upload_invalid_type",
          message:
            "Invalid file type. Allowed: PDF, PNG, JPG, JPEG, WEBP.",
          type: "error",
        });
        return false;
      }
      if (file.size / 1024 / 1024 > MAX_ONBOARDING_FILE_SIZE_MB) {
        openNotification?.({
          key: "onboarding_upload_size",
          message: `File size must be under ${MAX_ONBOARDING_FILE_SIZE_MB}MB.`,
          type: "error",
        });
        return false;
      }
      return true;
    },
    [openNotification]
  );

  /** Upload new file = replace current file (equivalent to remove old + upload new) */
  const handleOnboardingFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !onSelectOnboardingForm) return;
      if (!beforeUploadOnboarding(file)) {
        e.target.value = "";
        return;
      }
      onSelectOnboardingForm(file);
      e.target.value = "";
    },
    [onSelectOnboardingForm, beforeUploadOnboarding]
  );

  const handleUploadOnboardingClick = useCallback(() => {
    if (!canUploadOnboarding || isUploadingOnboarding || !canUpdateProfile) return;
    onboardingUploadRef.current?.click();
  }, [canUploadOnboarding, isUploadingOnboarding, canUpdateProfile]);

  const handleRemoveFile = useCallback(() => {
    if (hasPendingOnboardingFile) {
      onRemoveOnboardingForm?.();
    } else if (hasOnboardingFile && onRemoveExistingOnboardingForm) {
      onRemoveExistingOnboardingForm();
    }
  }, [
    hasPendingOnboardingFile,
    hasOnboardingFile,
    onRemoveOnboardingForm,
    onRemoveExistingOnboardingForm,
  ]);
  return (
    <Form
      style={{ width: "100%" }}
      form={form}
      onFinish={onSubmit}
      onValuesChange={() => {
        setIsDirty?.(true);
        clearFieldErrorMessageOnChange(form);
        setIsAllFieldsFilled(checkAllFieldsFilled());
      }}
      className="form-profile"
    >
      <Row gutter={{ xs: 8, sm: 30, md: 60 }}>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="Email"
            name="email"
            validateTrigger="onBlur"
            rules={emailRules}
          >
            <Input disabled placeholder="e.g. Smith, Johnson, Brown" />
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="UID"
            name="smartUID"
            validateTrigger="onBlur"
            className="user-code"
          >
            <Input
              disabled
              suffix={
                copied ? (
                  <SuccessIcon style={{ width: 24, height: 24 }} />
                ) : (
                  <img
                    src={CopyIcon}
                    style={{ cursor: "pointer" }}
                    onClick={handleCopy}
                  />
                )
              }
              placeholder="e.g. Smith, Johnson, Brown"
              className="user-co"
            />
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="Full Name/ Company Name"
            validateTrigger="onBlur"
            name="fullName"
            rules={companyNameRules}
          >
            <Input
              disabled={disabledInput}
              placeholder="Enter the full legal name of the company"
            />
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="Reg. No"
            validateTrigger="onBlur"
            name="regNo"
            rules={companyRegistrationNumberRules}
          >
            <Input
              disabled={disabledInput}
              placeholder="Enter the official company registration number"
            />
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="Company's Address"
            name="address"
            validateTrigger="onBlur"
            rules={addressRules}
          >
            <Input
              disabled={disabledInput}
              placeholder="Enter the address of the company"
            />
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="Country of incorporation"
            name="country"
            validateTrigger="onBlur"
            rules={[
              {
                required: true,
                message: "This field is required",
              },
            ]}
          >
            <Select
              disabled={disabledInput}
              showSearch
              placeholder="Select the country where the company is officially registered"
              suffixIcon={<ArrowIcon />}
              onChange={handleChangeCountry}
              optionLabelProp="label"
              filterOption={(input, option) => {
                const label = option?.label;
                if (typeof label === "string") {
                  return label.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              {metadataCountries.map((item) => (
                <Select.Option
                  key={item.value}
                  value={item.value}
                  label={item.label}
                >
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {item.label}
                    </span>
                    {searchCountry === item.value && (
                      <CheckSelectIcon style={{ flexShrink: 0 }} />
                    )}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <Form.Item
            label="Website"
            name="website"
            validateTrigger="onBlur"
            rules={websiteRules}
          >
            <Input placeholder="Enter the company's official website URL" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
        </Col>
        {/* Onboarding Form: status from /profile, upload to UserStorageFolder.OnboardingSMARTForm */}
        <Col style={{ marginBottom: "24px" }} xs={24} md={12}>
          <div className="onboarding-form-section">
            <div className="onboarding-form-header">
              <Typography className="onboarding-form-label">
                Onboarding Form
              </Typography>
              {onboardingStatus && ONBOARDING_STATUS_LABEL[onboardingStatus] && (
                <span
                  className="onboarding-form-status"
                  style={{
                    color: COLOR_ONBOARDING_STATUS[onboardingStatus] ?? "#fff",
                    backgroundColor: "#FFFFFF0D",
                  }}
                >
                  {ONBOARDING_STATUS_LABEL[onboardingStatus]}
                </span>
              )}
            </div>
            <div className="list-file-upload">
              <div
                style={{
                  marginBottom: "24px",
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                {canUploadOnboarding && (
                  <div className="onboarding-form-controls">
                    <input
                      ref={onboardingUploadRef}
                      type="file"
                      accept={ALLOWED_ONBOARDING_FILE_TYPES.join(",")}
                      style={{ display: "none" }}
                      onChange={handleOnboardingFileChange}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      className="upload-file onboarding-upload-btn"
                      onClick={handleUploadOnboardingClick}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleUploadOnboardingClick()
                      }
                      style={{
                        cursor: isOnboardingUploadDisabled
                          ? "not-allowed"
                          : "pointer",
                        opacity: isOnboardingUploadDisabled ? 0.7 : 1,
                      }}
                      aria-disabled={isOnboardingUploadDisabled}
                      data-disabled={isOnboardingUploadDisabled}
                    >
                      <img
                        src={CloudArrowUp}
                        alt=""
                        style={{ width: 24, height: 24 }}
                      />
                      <Typography
                        style={{
                          fontSize: 16,
                          fontWeight: 400,
                          color: "white",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Upload file
                      </Typography>
                    </div>
                  </div>
                )}
                {!onboardingFormRemoved && (hasPendingOnboardingFile || hasOnboardingFile && onboardingFileHref) && (
                  <div
                    className="list-file-upload-item-container"
                    style={{ minWidth: 0, overflow: "hidden", flex: 1 }}
                  >
                    {hasPendingOnboardingFile ? (
                      <Typography
                        className="onboarding-form-file-name"
                        title={pendingOnboardingFileName ?? undefined}
                        style={{
                          fontSize: 16,
                          fontWeight: 400,
                          color: "white",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          direction: "ltr",
                          textAlign: "left",
                          display: "block",
                          minWidth: 0,
                        }}
                      >
                        {pendingOnboardingFileName}
                      </Typography>
                    ) : hasOnboardingFile && onboardingFileHref ? (
                      <a
                        href={onboardingFileHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="onboarding-form-file-link"
                        style={{
                          fontSize: 16,
                          fontWeight: 400,
                          color: "white",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          direction: "ltr",
                          textAlign: "left",
                          display: "block",
                          minWidth: 0,
                        }}
                      >
                        {onboarding_file_name || onboardingDisplayName}
                      </a>
                    ) : (
                      <></>
                    )}
                    {!isOnboardingUploadDisabled ? (
                      <TrashIcon
                        className="icon-animation trans-icon"
                        onClick={handleRemoveFile}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <div className="form-edit-info-footer">
        <ButtonOutlinedCustom
          text={"Cancel"}
          size={isTablet ? "medium" : "large"}
          width={136}
          onClick={() => {
            if (isDirty) setShowModalControl(true);
            else navigate("/home");
          }}
        />
        <CustomButton
          type="submit"
          text={"Save"}
          size={isTablet ? "medium" : "large"}
          width={192}
          disabled={!isAllFieldsFilled || isSuccess}
        />
      </div>
    </Form>
  );
};

export default DetailInfo;
